import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getCrmMetrics } from '../api/client'
import PageHeader from '../components/PageHeader'
import { currentLocale } from '../i18n'

const STAGE_ORDER = ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost']
const STAGE_COLOR = {
  new:        'bg-gray-200',
  contacted:  'bg-sky-300',
  qualified:  'bg-violet-400',
  proposal:   'bg-amber-400',
  won:        'bg-emerald-500',
  lost:       'bg-red-300',
}
const ACTIVITY_ICON = { call: '📞', email: '✉', meeting: '📅', note: '📝' }
const STATUS_COLOR = {
  todo:        'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  done:        'bg-emerald-100 text-emerald-700',
}

function fmtMoney(value, currency) {
  if (!value) return '—'
  return `${Math.round(value).toLocaleString()} ${currency}`
}

function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })
}

function dayDelta(iso) {
  if (!iso) return null
  const d = new Date(iso + 'T00:00:00')
  return Math.round((d - new Date().setHours(0, 0, 0, 0)) / 86400000)
}

function valueLine(byCurrency) {
  if (!byCurrency || Object.keys(byCurrency).length === 0) return ''
  return Object.entries(byCurrency)
    .map(([c, v]) => `${Math.round(v).toLocaleString()} ${c}`)
    .join('  ·  ')
}

export default function CRMDashboard() {
  const { t } = useTranslation(['crm', 'common'])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    setLoading(true)
    getCrmMetrics()
      .then(d => { if (mounted) setData(d) })
      .catch(e => { if (mounted) setError(e.response?.data?.detail || t('dashboard.loadFailed')) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [])

  if (loading) return <div className="p-8 text-sm text-gray-400">{t('common:actions.loading')}</div>
  if (error)   return <div className="p-8 text-sm text-red-500">{error}</div>
  if (!data)   return null

  const { my, team, my_tasks, recent_activity } = data

  const myWonLine   = valueLine(my.won_value_by_currency)
  const teamWonLine = valueLine(team.won_value_by_currency)

  return (
    <div className="p-8 space-y-6">
      <PageHeader title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />

      {/* Top stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          label={t('dashboard.myOpenLeads')}
          value={my.open_count}
          sub={t('dashboard.openAcrossTeam', { count: team.open_count })}
          accent="text-violet-700"
        />
        <StatCard
          label={t('dashboard.wonYtdMine')}
          value={my.won_count}
          sub={myWonLine || t('dashboard.noValueRecorded')}
          accent="text-emerald-600"
        />
        <StatCard
          label={t('dashboard.lostYtdMine')}
          value={my.lost_count}
          sub={t('dashboard.lostAcrossTeam', { count: team.lost_count })}
          accent={my.lost_count ? 'text-red-500' : 'text-gray-500'}
        />
        <StatCard
          label={t('dashboard.teamWonYtd')}
          value={team.won_count}
          sub={teamWonLine || t('dashboard.noValueRecorded')}
          accent="text-gray-900"
        />
      </div>

      {/* Funnel — stage rows with mine vs team */}
      <Section title={t('dashboard.funnelTitle')} subtitle={t('dashboard.funnelSubtitle')}>
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
          {(() => {
            const teamMax = Math.max(1, ...STAGE_ORDER.map(s => team.by_stage_count?.[s] || 0))
            return STAGE_ORDER.map(stage => {
              const mine = my.by_stage_count?.[stage] || 0
              const all  = team.by_stage_count?.[stage] || 0
              const myPct   = (mine / teamMax) * 100
              const teamPct = (all  / teamMax) * 100
              const myVal   = my.by_stage_value?.[stage]
              return (
                <div key={stage} className="flex items-center gap-3">
                  <div className="w-24 text-xs font-medium text-gray-700 shrink-0">{t(`stages.${stage}`)}</div>
                  <div className="flex-1 relative h-7 bg-gray-50 rounded-md overflow-hidden">
                    {/* team bar */}
                    <div
                      className={`absolute inset-y-0 left-0 ${STAGE_COLOR[stage]} opacity-30 transition-all`}
                      style={{ width: `${teamPct}%` }}
                    />
                    {/* mine bar */}
                    <div
                      className={`absolute inset-y-0 left-0 ${STAGE_COLOR[stage]} transition-all`}
                      style={{ width: `${myPct}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-between px-3 text-xs">
                      <span className="font-semibold text-gray-900 tabular-nums">{mine}</span>
                      <span className="text-gray-500 tabular-nums">{t('dashboard.teamCount', { count: all })}</span>
                    </div>
                  </div>
                  <div className="w-32 text-right text-xs text-gray-500 tabular-nums shrink-0 truncate">
                    {valueLine(myVal) || <span className="text-gray-300">—</span>}
                  </div>
                </div>
              )
            })
          })()}
        </div>
      </Section>

      {/* Tasks + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* My tasks */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{t('dashboard.tasksTitle')}</h3>
              <p className="text-[11px] text-gray-400">{t('dashboard.tasksSubtitle')}</p>
            </div>
            <Link to="/crm/tasks" className="text-xs text-gray-500 hover:text-gray-900">{t('dashboard.allTasks')}</Link>
          </div>
          {(my_tasks.overdue.length === 0 && my_tasks.due_this_week.length === 0) ? (
            <p className="text-sm text-gray-400 px-4 py-8 text-center">{t('dashboard.noTasks')}</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {my_tasks.overdue.map(task => <TaskRow key={task.id} task={task} late />)}
              {my_tasks.due_this_week.map(task => <TaskRow key={task.id} task={task} />)}
            </ul>
          )}
        </div>

        {/* Recent activity */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">{t('dashboard.activityTitle')}</h3>
            <p className="text-[11px] text-gray-400">{t('dashboard.activitySubtitle')}</p>
          </div>
          {recent_activity.length === 0 ? (
            <p className="text-sm text-gray-400 px-4 py-8 text-center">{t('dashboard.noActivity')}</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {recent_activity.map(a => (
                <li key={a.id} className="px-4 py-3 hover:bg-gray-50/60 transition-colors">
                  <div className="flex items-start gap-3">
                    <span className="text-base shrink-0">{ACTIVITY_ICON[a.type] || '•'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-400">
                        {a.company_name ? (
                          <Link to={`/crm`} className="hover:underline font-medium text-gray-700">{a.company_name}</Link>
                        ) : t('dashboard.leadFallback')}
                        <span className="mx-1">·</span>
                        <span>{fmtDate(a.activity_date)}</span>
                      </div>
                      <p className="text-sm text-gray-800 mt-0.5 line-clamp-2">{a.summary}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

// ── helpers ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent = 'text-gray-900' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{label}</div>
      <div className={`mt-2 text-3xl font-bold ${accent} leading-tight tabular-nums`}>{value}</div>
      {sub && <div className="mt-2 text-xs text-gray-500 truncate" title={sub}>{sub}</div>}
    </div>
  )
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="mb-3">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}

function TaskRow({ task, late = false }) {
  const { t } = useTranslation('crm')
  const delta = dayDelta(task.deadline)
  return (
    <li className="px-4 py-3 hover:bg-gray-50/60 transition-colors">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{task.name}</p>
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
            <span className={`px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLOR[task.status] || 'bg-gray-100 text-gray-500'}`}>
              {task.status === 'in_progress' ? t('dashboard.statusInProgress') : task.status === 'todo' ? t('dashboard.statusTodo') : task.status}
            </span>
            {task.deadline && (
              <span className={late ? 'text-red-600 font-medium' : 'text-gray-500'}>
                {fmtDate(task.deadline)}
                {late && delta != null ? ` (${t('dashboard.daysLate', { count: -delta })})` : ''}
              </span>
            )}
          </div>
        </div>
        {late && <span className="text-red-500 text-sm">⚠</span>}
      </div>
    </li>
  )
}
